import flat from "array.prototype.flat";
import { fromEntries } from "./object-entries";

type LineIndex = number;
type ColumnIndex = number;

export function parseFocus(focus: string) {
  if (!focus) {
    throw new Error("Focus cannot be empty");
  }

  try {
    const parts = focus.split(/,(?![^\[]*\])/g).map(parsePart);
    return fromEntries(flat(parts));
  } catch (error) {
    // TODO enhance error
    throw error;
  }
}

type Part = [LineIndex, true | ColumnIndex[]];

function parsePart(part: string): Part[] {
  // a part could be
  // - a line number: "2"
  // - a line range: "5:9"
  // - a line number with a column selector: "2[1,3:5,9]"
  const columnsMatch = part.match(/(\d+)\[(.+)\]/);
  if (columnsMatch) {
    const [, line, columns] = columnsMatch;
    const columnsList = columns.split(",").map(expandString);
    const lineIndex = Number(line) - 1;
    const columnIndexes = flat(columnsList).map(c => c - 1);
    return [[lineIndex, columnIndexes]];
  } else {
    return expandString(part).map(lineNumber => [lineNumber - 1, true]);
  }
}

function expandString(part: string) {
  // Transforms something like
  // - "1:3" to [1,2,3]
  // - "4" to [4]
  const [start, end] = part.split(":");

  if (!isNaturalNumber(start)) {
    throw new FocusNumberError(start);
  }

  const startNumber = Number(start);

  if (startNumber < 1) {
    throw new LineOrColumnNumberError();
  }

  if (!end) {
    return [startNumber];
  } else {
    if (!isNaturalNumber(end)) {
      throw new FocusNumberError(end);
    }

    const list: number[] = [];
    for (let i = startNumber; i <= +end; i++) {
      list.push(i);
    }
    return list;
  }
}

function isNaturalNumber(n: any) {
  n = n.toString(); // force the value in case it is not
  var n1 = Math.abs(n),
    n2 = parseInt(n, 10);
  return !isNaN(n1) && n2 === n1 && n1.toString() === n;
}

export function getFocusSize(focus: Record<LineIndex, true | ColumnIndex[]>, skipRowsList: [number, number][]) {
  const lineIndexList = Object.keys(focus).map(k => +k);
  const focusStart = Math.min.apply(Math, lineIndexList);
  const focusEnd = Math.max.apply(Math, lineIndexList);
  const skippedList = skipRowsList.reduce((result: number[], rowPair) => {
    for (let i = rowPair[0]; i <= rowPair[1]; i++) {
      if (result.indexOf(i) === -1) {
        result.push(i);
      }
    }
    return result;
  }, []);
  const innerSkippedList = skippedList.filter(line => line >= (focusStart + 1) && line <= (focusEnd + 1));
  const upperSkippedList = skippedList.filter(line => line < (focusStart + 1));
  var focusCenter = ((focusStart + focusEnd + 1 - innerSkippedList.length) / 2) - upperSkippedList.length;
  var focusCount = focusEnd - focusStart + 1 - innerSkippedList.length;
  return {
    focusCenter,
    focusCount
  };
}

export class LineOrColumnNumberError extends Error {
  constructor() {
    super(`Invalid line or column number in focus string`);
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class FocusNumberError extends Error {
  number: string;
  constructor(number: string) {
    super(`Invalid number "${number}" in focus string`);
    this.number = number;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}
